import React, { useState, useEffect, useMemo } from 'react';
import StockTable from './StockTable';
import ETFTable from './ETFTable';
import CashTable from './CashTable';
import EarningsTable from './EarningsTable';
import OptionTable from './OptionTable';
import { formatCurrency, calculateMarketValue } from './helpers';
import './Spinner.css';
import './PortfolioTables.css';

function PortfolioTables() {
  const [portfolio, setPortfolio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTicker, setNewTicker] = useState('');
  const [newQuantity, setNewQuantity] = useState('');
  const [newType, setNewType] = useState('stock'); 
  const [newStrike, setNewStrike] = useState('');
  const [newExpiration, setNewExpiration] = useState('');
  const [newPremium, setNewPremium] = useState('');
  const [optionType, setOptionType] = useState('put');
  const [editingPosition, setEditingPosition] = useState(null); 
  const [editQuantity, setEditQuantity] = useState(''); 
  
  // 1. ADDED state for editing the premium
  const [editPremium, setEditPremium] = useState(''); 
  
  const [riskTolerance, setRiskTolerance] = useState('medium'); 
  const [investmentPhilosophy, setInvestmentPhilosophy] = useState('balanced'); 

  const fetchPortfolio = async () => {
      setLoading(true);
      try {
        const response = await fetch('https://api.ranzhao.com/positions', {
          credentials: 'include',
        });
        const data = await response.json();
        setPortfolio(data);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
      } finally {
        setLoading(false);
      }
    };

  const fetchUserProfile = async () => {
    try {
      const response = await fetch('https://api.ranzhao.com/user/profile', {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setRiskTolerance(data.risk_tolerance || 'medium');
        setInvestmentPhilosophy(data.investment_philosophy || 'balanced');
      } else {
        console.error('Error fetching user profile:', response.statusText);
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  useEffect(() => {
    Promise.all([fetchPortfolio(), fetchUserProfile()])
      .then(() => setLoading(false))
      .catch(() => setLoading(false)); 
  }, []);

  const stocks = useMemo(() => portfolio ? portfolio.filter((item) => item.type === 'stock') : [], [portfolio]);
  const etfs = useMemo(() => portfolio ? portfolio.filter((item) => item.type === 'etf') : [], [portfolio]);
  const cash_positions = useMemo(() => portfolio ? portfolio.filter((item) => item.type === 'cash') : [], [portfolio]);
  const options = useMemo(() => portfolio ? portfolio.filter((item) => (item.type === 'option_stock'||item.type === 'option_etf')) : [], [portfolio]);
  //  Group the "Core" portfolio (Stocks, ETFs, Cash)
  const corePortfolio = useMemo(() => [...stocks, ...etfs, ...cash_positions], [stocks, etfs, cash_positions]);

  const totalMarketValue = useMemo(() => {
        let total = 0;
        stocks.forEach(item => {
            total += calculateMarketValue(item.quantity, item.details?.Price);
        });
        etfs.forEach(item => {
            total += calculateMarketValue(item.quantity, item.details?.Price);
        });
        cash_positions.forEach(item => {
            total += calculateMarketValue(item.quantity, '1'); 
        });
        return total;
  }, [stocks, etfs, cash_positions]);

  const corePortfolioJson = useMemo(() => JSON.stringify(corePortfolio), [corePortfolio]);
  const optionsPortfolioJson = useMemo(() => JSON.stringify(options), [options]);
  const fullPortfolioJson = useMemo(() => JSON.stringify(portfolio), [portfolio]);

  const handleAddPosition = async () => {
    if (newTicker && newQuantity && newType) {
      if ((newType === 'option_stock'||newType ==='option_etf') && (!newStrike || !newExpiration)) {
        alert('Please provide strike and expiration for options.');
        return;
      }
      try {
        const response = await fetch('https://api.ranzhao.com/positions', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams({
            position: newTicker,
            quantity: newQuantity,
            type: newType,
            option_type: (newType === 'option_stock'||newType==='option_etf') ? optionType : 'none',
            strike: (newType === 'option_stock'||newType==='option_etf') ? newStrike : 0,
            expiry: (newType === 'option_stock'||newType==='option_etf') ? newExpiration : '9999-12-31',
            option_premium: (newType === 'option_stock'||newType==='option_etf') ? (newPremium || 0) : 0, 
          }),
        });
        if (response.ok) {
          fetchPortfolio();
          setNewTicker('');
          setNewQuantity('');
          setNewStrike('');
          setNewExpiration('');
          setNewPremium(''); 
          setNewType('stock');
        } else {
          console.error('Error adding position:', response.statusText);
        }
      } catch (error) {
        console.error('Error adding position:', error);
      }
    } else {
      console.warn('Please fill in all fields.');
    }
  };

  const handleUpdatePosition = async (item) => {
      if (item) {
        try {
          const params = new URLSearchParams({
            position: item.position,
            type: item.type,
            option_type: item.option_type || 'none',
            strike: item.strike_price || 0,
            expiry: item.expiration_date || '9999-12-31'
          });

          const response = await fetch(`https://api.ranzhao.com/positions?${params.toString()}`, {
            method: 'PUT',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              quantity: editQuantity,
              // 2. ADDED editPremium to the PUT payload
              option_premium: editPremium, 
            }),
          });
          if (response.ok) {
            fetchPortfolio();
            setEditingPosition(null);
            setEditQuantity('');
            setEditPremium(''); // 3. Reset after successful update
          } else {
            console.error('Error updating position:', response.statusText);
          }
        } catch (error) {
          console.error('Error updating position:', error);
        }
      } else {
        console.warn('Please enter a new quantity or premium.');
      }
    };

  const handleDeletePosition = async (item) => {
    try {
    const params = new URLSearchParams({
      position: item.position,
      type: item.type,
      option_type: item.option_type || 'none',
      strike: item.strike_price || 0,
      expiry: item.expiration_date || '9999-12-31'
    });
    const response = await fetch(`https://api.ranzhao.com/positions?${params.toString()}`, {
      method: 'DELETE',
      credentials: 'include',
    });
      if (response.ok) {
        fetchPortfolio();
      } else {
        console.error('Error deleting position:', response.statusText);
      }
    } catch (error) {
      console.error('Error deleting position:', error);
    }
  };

  const handleUpdateUserProfile = async () => {
    try {
     const response = await fetch('https://api.ranzhao.com/user/profile', { 
      method: 'PUT',
      credentials: 'include',
      headers: {
       'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
       risk_tolerance: riskTolerance,
       investment_philosophy: investmentPhilosophy,
      }),
     });
     if (response.ok) {
      console.log('User profile updated successfully!');
     } else {
      console.error('Error updating user profile:', response.statusText);
     }
    } catch (error) {
     console.error('Error updating user profile:', error);
    }
   };

  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      alert('Portfolio data and user profile copied to clipboard!');
    } catch (err) {
      console.error('Fallback copy failed:', err);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const copyToClipboard = (instruction = "Please suggest how I can re-balance my full portfolio.", myPortfolio = fullPortfolioJson) => {
  // Inject the 'instruction' variable at the end of the payload
  const payload = `I am a ${riskTolerance} risk investor with a ${investmentPhilosophy} investment philosophy. I have a portfolio with a total market value of ${formatCurrency(totalMarketValue)}. Request: ${instruction} Portfolio: ${myPortfolio}`;

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(payload)
      .then(() => {
        alert('Portfolio data and user profile copied to clipboard!');
      })
      .catch((error) => {
        console.error('Clipboard write failed:', error);
      });
  } else {
    fallbackCopyToClipboard(payload);
  }
};

const isTesla = useMemo(() => {
  // This looks for "Linux" anywhere in the string, ignoring upper/lowercase
  return /Linux/i.test(navigator.userAgent);
}, []);

const handleTeslaSelect = (currentValue, setter) => (e) => {
  // Target ONLY Tesla.
  if (isTesla) {
    e.preventDefault();

    const optionsArray = Array.from(e.currentTarget.options);
    const order = optionsArray.map(opt => opt.value);

    // Find the index of the currently selected value
    let currentIndex = order.indexOf(currentValue);

    // Safety fallback just in case the current value isn't in the list
    if (currentIndex === -1) currentIndex = 0;

    const nextIndex = (currentIndex + 1) % order.length;

    // Dynamically call the correct state setter
    setter(order[nextIndex]);
  }
};


  if (loading) {
  return <div className="spinner" aria-label="Loading..."></div>;
  }

  return (
    <div>
      <EarningsTable stocks={stocks} options={options}/>

      <div>
        <h2>Add Position</h2>
        <input
          type="text"
          placeholder="Ticker"
          value={newTicker}
          onChange={(e) => {
          const sanitizedValue = e.target.value.toUpperCase().replace(/[^A-Z]/g, '');
          setNewTicker(sanitizedValue);
          }}
        />
        <input
          type="number"
          placeholder="Quantity"
          value={newQuantity}
          onChange={(e) => setNewQuantity(e.target.value)}
        />
<select 
      value={newType} 
      onChange={(e) => setNewType(e.target.value)}
      // Touch start is best for the physical car screen
      onTouchStart={handleTeslaSelect(newType, setNewType)}
    >
      <option value="stock">Stock</option>
      <option value="etf">ETF</option>
      <option value="cash">Cash</option>
      <option value="option_stock">Stock Option</option>
      <option value="option_etf">ETF Option</option>
    </select>

        {(newType === 'option_stock' || newType === 'option_etf') && (
          <span style={{ marginLeft: '10px' }}>
            <select value={optionType} onChange={(e) => setOptionType(e.target.value)} onTouchStart={handleTeslaSelect(optionType,setOptionType)}>
              <option value="put">Put</option>
              <option value="call">Call</option>
            </select>
            <input
              type="number"
              placeholder="Strike"
              style={{ width: '80px', marginLeft: '5px' }}
              value={newStrike}
              onChange={(e) => setNewStrike(e.target.value)}
            />
            <input
              type={isTesla? "text":"date"}
	      placeholder={isTesla?"YYYY-MM-DD":""}
              style={{ marginLeft: '5px' }}
              value={newExpiration}
              onChange={(e) => {
		      const v = e.target.value;
		      if (isTesla) {
			// Clean up the text input to ensure only numbers and dashes make it to your state
      			const cleanValue = v.replace(/[^0-9-]/g, '');
      			setNewExpiration(cleanValue);
		      } else {
		      	setNewExpiration(v)
		      }
	      }}
            />
            <input
              type="number"
              step="0.01" 
              placeholder="Premium"
              style={{ width: '80px', marginLeft: '5px' }}
              value={newPremium}
              onChange={(e) => setNewPremium(e.target.value)}
            />
          </span>
        )}

        <button onClick={handleAddPosition} style={{ marginLeft: '5px' }}>Add</button>
      </div>

      <OptionTable 
        options={options} 
        onDelete={handleDeletePosition} 
        onEdit={setEditingPosition} 
        onUpdate={handleUpdatePosition} 
        editQuantity={editQuantity} 
        setEditQuantity={setEditQuantity} 
        // 4. ADDED editPremium props here
        editPremium={editPremium}
        setEditPremium={setEditPremium}
        editingPosition={editingPosition}
      />
      
      <StockTable stocks={stocks} onDelete={handleDeletePosition} onEdit={setEditingPosition} onUpdate={handleUpdatePosition} editQuantity={editQuantity} setEditQuantity={setEditQuantity} editingPosition={editingPosition}/>
      <ETFTable etfs={etfs} onDelete={handleDeletePosition} onEdit={setEditingPosition} onUpdate={handleUpdatePosition} editQuantity={editQuantity} setEditQuantity={setEditQuantity} editingPosition={editingPosition}/>
      <CashTable cash_positions={cash_positions} onDelete={handleDeletePosition} onEdit={setEditingPosition} onUpdate={handleUpdatePosition} editQuantity={editQuantity} setEditQuantity={setEditQuantity} editingPosition={editingPosition}/>
      
      <p>Total Market Value: {formatCurrency(totalMarketValue)}</p>
      
      <div>
        <h2>Investor Profile</h2>
        <p>Investment Philosophy:
          <select value={investmentPhilosophy} onChange={(e) => setInvestmentPhilosophy(e.target.value)} onTouchStart={handleTeslaSelect(investmentPhilosophy,setInvestmentPhilosophy)} >
          <option value="value">Value</option>
          <option value="balanced">Balanced</option>
          <option value="growth">Growth</option>
          </select>
        </p>
        <p>Risk Tolerance:
        <select value={riskTolerance} onChange={(e) => setRiskTolerance(e.target.value)} onTouchStart={handleTeslaSelect(riskTolerance,setRiskTolerance)}>
          <option value="ultra-low">Ultra-Low</option>
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="ultra-high">Ultra-High</option>
        </select>
        </p>
        <button onClick={() => handleUpdateUserProfile()}>Update Profile</button>
      </div>

      <h2> Gen AI Prompts </h2>
      <button onClick={() => copyToClipboard("Please suggest how I can re-balance my core portfolio.", corePortfolioJson)}>Rebalance my portfolio</button>
<button onClick={() =>copyToClipboard("Please analyze my options portfolio, bucket my monthly premium income, and suggest how to maximize income while minimizing risk.",optionsPortfolioJson)}>Analyze my options</button>
       
    </div>
  );
}

export default PortfolioTables;
